import { Mail, Phone, MapPin, GraduationCap } from "lucide-react";
import { useBranding } from "@/context/BrandingContext";

type FooterVariant = "full" | "compact";

function safeExternalHref(url: string | undefined): string | undefined {
    if (!url) return undefined;
    try {
        const parsedUrl = new URL(url);
        if (parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:") {
            return url;
        }
        return undefined;
    } catch {
        return undefined;
    }
}

const SOCIAL_ITEMS = [
    {
        key: "facebook",
        label: "Facebook",
        path: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z",
    },
    {
        key: "twitter",
        label: "X (Twitter)",
        path: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z",
    },
    {
        key: "instagram",
        label: "Instagram",
        path: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z",
    },
    {
        key: "linkedin",
        label: "LinkedIn",
        path: "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z",
    },
    {
        key: "youtube",
        label: "YouTube",
        path: "M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z",
    },
] as const;

function FooterContent({ variant }: { variant: FooterVariant }) {
    const { branding, loading } = useBranding();
    const isCompact = variant === "compact";

    const universityName = branding?.name || "University Portal";
    const logoUrl = branding?.branding?.logo;
    const socialMedia = branding?.branding?.social_media || {};
    const accentColor = branding?.branding?.colors?.accent || "#517dee";
    const footerBgColor = branding?.branding?.colors?.footer_background || "#1d3658";

    // Contact information from API (nested inside branding.branding)
    const contactEmail = branding?.branding?.contact_email;
    const phoneNumber = branding?.branding?.phone_number;
    const address = branding?.branding?.address;
    const footerText =
        branding?.branding?.footer_text ||
        "Empowering the next generation of leaders through innovation, excellence, and community.";
    const privacyPolicyUrl = safeExternalHref(branding?.branding?.privacy_policy_url);
    const termsOfServiceUrl = safeExternalHref(branding?.branding?.terms_of_service_url);
    const accessibilityUrl = safeExternalHref(branding?.branding?.accessibility_url);
    const hasContactInfo = Boolean(address || phoneNumber || contactEmail);

    return (
        <footer
            className={isCompact ? "text-white pt-8 pb-4 text-xs" : "text-white pt-16 pb-8"}
            style={{ backgroundColor: footerBgColor }}
        >
            <div className="container mx-auto px-4 md:px-6">
                <div
                    className={
                        isCompact
                            ? "grid grid-cols-1 md:grid-cols-4 gap-6 mb-6"
                            : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12"
                    }
                >
                    {/* Brand Column */}
                    <div className={isCompact ? "md:col-span-3 space-y-3" : "lg:col-span-3 space-y-6"}>
                        <div className={isCompact ? "flex items-center gap-2" : "flex items-center gap-3"}>
                            <div
                                className={
                                    isCompact
                                        ? "w-7 h-7 flex items-center justify-center shrink-0"
                                        : "relative overflow-hidden w-12 h-12 flex items-center justify-center"
                                }
                            >
                                {loading ? (
                                    <div className={isCompact ? "w-7 h-7 bg-white/20 animate-pulse rounded" : "h-12 w-12 bg-white/20 animate-pulse rounded"} />
                                ) : logoUrl ? (
                                    <img
                                        src={logoUrl}
                                        alt={`${universityName} Logo`}
                                        className={isCompact ? "h-7 w-auto object-contain" : "h-12 w-auto max-w-none object-cover object-left"}
                                    />
                                ) : (
                                    <div
                                        className={
                                            isCompact
                                                ? "w-7 h-7 rounded flex items-center justify-center text-white bg-white/20"
                                                : "w-12 h-12 rounded flex items-center justify-center text-white bg-white/20"
                                        }
                                    >
                                        <GraduationCap className={isCompact ? "w-4 h-4" : "w-8 h-8"} />
                                    </div>
                                )}
                            </div>
                            <span
                                className={isCompact ? "text-sm font-bold text-white" : "text-2xl font-bold text-white"}
                                style={{ fontFamily: "var(--font-university-name)" }}
                            >
                                {universityName}
                            </span>
                        </div>

                        <p className={isCompact ? "text-white/60 leading-relaxed max-w-md" : "text-white/70 leading-relaxed max-w-md"}>
                            {footerText}
                        </p>

                        <div className={isCompact ? "flex gap-2" : "flex gap-4"}>
                            {SOCIAL_ITEMS.map((item) => {
                                const rawLink = socialMedia[item.key as keyof typeof socialMedia];
                                const link = typeof rawLink === "string" ? safeExternalHref(rawLink) : undefined;
                                if (!link) return null;

                                return (
                                    <a
                                        key={item.key}
                                        href={link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={
                                            isCompact
                                                ? "w-7 h-7 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#00A3FF] transition-colors"
                                                : "h-10 w-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#00A3FF] hover:text-white transition-all duration-300"
                                        }
                                        aria-label={item.label}
                                    >
                                        <svg className={isCompact ? "w-3.5 h-3.5" : "w-[18px] h-[18px]"} fill="currentColor" viewBox="0 0 24 24">
                                            <path d={item.path} />
                                        </svg>
                                    </a>
                                );
                            })}
                        </div>
                    </div>

                    {/* Contact */}
                    {hasContactInfo && (
                        <div className={isCompact ? "space-y-2" : ""}>
                            <h3 className={isCompact ? "text-white font-bold text-[11px] uppercase tracking-widest mb-3" : "text-lg font-bold mb-6 text-white font-serif"}>
                                {isCompact ? "Contact" : "Contact Us"}
                            </h3>
                            <ul className={isCompact ? "space-y-2 text-white/60" : "space-y-4"}>
                                {address && (
                                    <li className={isCompact ? "flex items-start gap-2" : "flex items-start gap-3 text-white/70"}>
                                        <MapPin
                                            className={isCompact ? "shrink-0 mt-0.5 w-3.5 h-3.5" : "shrink-0 mt-1"}
                                            size={isCompact ? undefined : 18}
                                            style={{ color: accentColor }}
                                        />
                                        <span style={{ whiteSpace: "pre-line" }}>{address}</span>
                                    </li>
                                )}
                                {phoneNumber && (
                                    <li className={isCompact ? "flex items-center gap-2" : "flex items-center gap-3 text-white/70"}>
                                        <Phone
                                            className={isCompact ? "shrink-0 w-3.5 h-3.5" : "shrink-0"}
                                            size={isCompact ? undefined : 18}
                                            style={{ color: accentColor }}
                                        />
                                        <span>{phoneNumber}</span>
                                    </li>
                                )}
                                {contactEmail && (
                                    <li className={isCompact ? "flex items-center gap-2" : "flex items-center gap-3 text-white/70"}>
                                        <Mail
                                            className={isCompact ? "shrink-0 w-3.5 h-3.5" : "shrink-0"}
                                            size={isCompact ? undefined : 18}
                                            style={{ color: accentColor }}
                                        />
                                        <span>{contactEmail}</span>
                                    </li>
                                )}
                            </ul>
                        </div>
                    )}
                </div>

                <div
                    className={
                        isCompact
                            ? "border-t border-white/10 pt-4 flex flex-col sm:flex-row justify-between items-center gap-2 text-white/40"
                            : "border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4"
                    }
                >
                    <p className={isCompact ? "" : "text-white/50 text-sm"}>
                        © {new Date().getFullYear()} {universityName}. All rights reserved.
                    </p>
                    <div className={isCompact ? "flex gap-4" : "flex gap-6 text-sm text-white/50"}>
                        {privacyPolicyUrl && (
                            <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                                Privacy Policy
                            </a>
                        )}
                        {termsOfServiceUrl && (
                            <a href={termsOfServiceUrl} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                                Terms of Service
                            </a>
                        )}
                        {accessibilityUrl && (
                            <a href={accessibilityUrl} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                                Accessibility
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </footer>
    );
}

export function Footer() {
    return <FooterContent variant="full" />;
}

export function PortalFooter() {
    return <FooterContent variant="compact" />;
}
